import http from 'http'
import { Map } from 'immutable'

const state = {
  /**
   * 分组应用的策略
   * {
   *   groupId: {strategy},
   *   groupId: {strategy}
   * }
   */
  strategyInGroup: Map(),

  /**
   * 设备应用策略
   * {
   *   deviceId: {strategy},
   *   deviceId: {strategy}
   * }
   */
  strategyInDevice: Map()
}

const getters = {
  /**
   * 分组应用的策略，具体字段查看 strategy 模块
   * @param {*String} groupId 分组id
   */
  strategyInGroup: state => ({ groupId } = {}) => {
    return state.strategyInGroup.get(groupId, Map()).toJS()
  },

  /**
   * 设备应用策略，具体字段查看 strategy 模块
   * @param {*String} deviceId 设备id
   */
  strategyInDevice: state => ({ deviceId } = {}) => {
    return state.strategyInDevice.get(deviceId, Map()).toJS()
  }
}

const actions = {
  /**
   * 获取分组的应用策略
   * @param {*String} groupId 分组id
   */
  async getStrategyInGroup({ commit }, { groupId } = {}) {
    try {
      const entities = await http.command.findStrategyInGroup(groupId)
      commit('set_strategy_in_group', entities)
      return entities.get(groupId, Map()).toJS()
    } catch (e) {
      console.log(e)
      throw new Error(e)
    }
  },

  /**
   * 获取设备的应用策略
   * @param {*String} deviceId 设备id
   */
  async getStrategyInDevice({ commit }, { deviceId } = {}) {
    try {
      const entities = await http.command.findStrategyInDevice(deviceId)
      commit('set_strategy_in_device', entities)
    } catch (e) {
      console.log(e)
      throw new Error(e)
    }
  },

  /**
   * 下发参数
   * @param {*String} options.id 设备id
   * @param {*Number} options.paramSettingMode 参数设置模式,1-默认；2-自定义（如果电池电压选其他此参数值则为2-自定义，否则为1-默认）
   * @param {*Number} options.batteryType 电池类型（1-磷酸铁锂；2-三元锂；3-铅酸；）
   * @param {*Number} options.batteryVoltage 电池电压（如电池类型选中磷酸铁锂有12V，24V，其他）
   * @param {*Number} options.batteryOverVoltage 电池超压电压（用于充电停止电压），单位：V
   * @param {*Number} options.batteryOverToBackVoltage 电池超压恢复电压，单位：V
   * @param {*Number} options.batteryLowVoltage 电池低压电压（用于停止亮灯电压），单位：V
   * @param {*Number} options.batteryLowToBackVoltage 电池低压恢复电压，单位：V
   * @param {*Number} options.ledRatedCurrent LED灯额定电流，单位：A
   * @param {*Number} options.dayThreshold 光控天亮阈值，单位：V
   * @param {*Number} options.nightThreshold 光控天黑阈值，单位：V
   * @param {Number} options.strongChargeVoltage 强充电压，单位：V
   * @param {Number} options.evenChargeVoltage 均衡充电压，单位：V
   * @param {Number} options.floatChargeVoltage 浮充电压，单位：V
   */
  async updateDeviceConfig({ commit }, options) {
    try {
      const entities = await http.command.updateConfig(options)
      commit('update_device', entities.get('devices', Map()))
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 对分组下发策略
   * @param {*String} groupId 分组id
   * @param {*String} strategyId 策略id
   */
  async updateGroupStrategy({ commit }, { groupId, strategyId }) {
    try {
      await http.command.updateGroupStrategy(groupId, strategyId)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 对设备下发策略
   * @param {*String} deviceId 设备id
   * @param {*String} strategyId 策略id
   */
  async updateDeviceStrategy({ commit }, { deviceId, strategyId }) {
    try {
      await http.command.updateDeviceStrategy(deviceId, strategyId)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 设置分组
   * @param {*String} options.projectId 项目id
   * @param {*String} options.groupId 分组id
   * @param {*Array} options.deviceId 修改后分组内的设备id列表
   */
  async updateDeviceInGroup({ commit }, { projectId, groupId, deviceId } = {}) {
    try {
      let data = await http.command.updateInGroup(projectId, groupId, deviceId)
      return data
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 启用、停用设备（批量）
   * @param {*String} options.projectId 项目id
   * @param {*Array} options.deviceId 设备id
   * @param {*String} options.type 更新后的状态，可选值（enable, disable）
   *
   * @return {Array} 修改失败的设备id列表，示例 ['1', '2']
   */
  async updateDeviceState({ commit }, { projectId, deviceId, type } = {}) {
    try {
      const { failIds } = await http.command.updateDeviceState(projectId, deviceId, type)
      const idsFail = failIds.map(item => item.toString())
      // const idsSuccess = successIds.map(item => item.toString())
      // commit('update_device_state', { deviceId: idsSuccess, newState: type })
      return idsFail
    } catch (err) {
      throw new Error(err)
    }
  },

  /**
   * 启用、停用设备（批量）
   * @param {*String} options.projectId 项目id
   * @param {*Array} options.deviceId 设备id
   * @param {*String} options.password
   */
  async destroyDeviceByBatch({ commit, dispatch }, { projectId, deviceId, password } = {}) {
    try {
      await http.command.destroyByIds(projectId, deviceId, password)
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 更新设备状态
   * @param {*String} projectId 项目id
   * @param {*Array} deviceId 设备id
   */
  async updateDeviceStatus({ commit, dispatch }, { projectId, deviceId }) {
    try {
      await http.command.updateStatus(projectId, deviceId)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      // await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 单个更新设备状态
   * @param {*String} projectId 项目id
   * @param {*Array} deviceId 设备id
   */
  async updateDeviceStatusBySingle({ commit, dispatch }, { projectId, deviceId }) {
    try {
      await http.command.updateStatusBySingle(deviceId)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 开、关、调光（批量）
   * @param {*String} projectId 项目id
   * @param {*Array} deviceId 设备id
   * @param {*String} type 操作类型，可选值：open, close, dimme
   * @param {Number} brightness 亮度 调光时，需要该参数
   */
  async updateDeviceLevel({ commit, dispatch }, { projectId, deviceId, type, brightness }) {
    try {
      await http.command.dimmeLevel(projectId, deviceId, type, brightness)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 开、关、调光（单个）
   * @param {*String} projectId 项目id
   * @param {*Array} deviceId 设备id
   * @param {*String} type 操作类型，可选值：open, close, dimme
   * @param {Number} brightness 亮度 调光时，需要该参数
   */
  async updateDeviceLevelBySingle({ commit, dispatch }, { projectId, deviceId, type, brightness }) {
    try {
      await http.command.dimmeLevelBySingle(deviceId, type, brightness)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 对分组进行开关、调光
   * @param {*String} groupId 分组id
   * @param {*Number} switchState 状态 1-开、2-关、3-调光
   * @param {*Number} brightness 亮度 调光时，需要该参数
   */
  async updateDimmeInGroup({ commit }, { groupId, switchState, brightness }) {
    try {
      await http.command.dimmeInGroup(groupId, switchState, brightness)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 批量下发智能功率参数
   * @param {*String} projectId 项目id
   * @param {*String} solarMoteIds 设备id数组
   * @param {*Number} batteryType 电池类型（1-磷酸铁锂；2-三元锂；3-铅酸；）
   * @param {*String} voltagePointIntervals 智能功率参数区间
   */
  async updatePowerParams({ commit }, { projectId, solarMoteIds, batteryType, voltagePointIntervals }) {
    try {
      return await http.command.setPowerParamInBatch(projectId, solarMoteIds, batteryType, voltagePointIntervals)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 批量下发零度充电方式
   * @param {*String} projectId 项目id
   * @param {*String} solarMoteIds 设备id数组
   * @param {*String} zeroChargeType 锂电池零度充电方式；0-正常充电；1-不充电；2-慢充电
   */
  async setZeroChargeType({ commit }, { projectId, solarMoteIds, zeroChargeType }) {
    try {
      return await http.command.setZeroChargeTypeInBatch(projectId, solarMoteIds, zeroChargeType)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 对设备下发人流量参数
   * @param {*String} deviceId 设备id
   * @param {*Number} trafficControl 人流量开关 0-关闭；1-开启
   */
  async updateTrafficParam({ commit }, { deviceId, trafficControl }) {
    try {
      return await http.command.updateTrafficParam(deviceId, trafficControl)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 对设备下发切换电源电压
   * @param {*String} deviceId 设备id
   * @param {*Number} switchingPowerSupplyVoltage 人流量开关 0-关闭；1-开启
   */
  async updatePowerSupplyVol({ commit }, { deviceId, switchingPowerSupplyVoltage }) {
    try {
      return await http.command.updatePowerSupplyVol(deviceId, switchingPowerSupplyVoltage)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 设置休眠状态（后面跟的是设备id）
   * @param {*String} deviceId 设备id
   * @param {*Number} sleepState 0-唤醒；1-休眠（非光控唤醒）；2-休眠（光控唤醒）
   */
  async updateSleepStateById({ commit }, { deviceId, sleepState }) {
    try {
      return await http.command.updateSleepStateById(deviceId, sleepState)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 设置休眠状态（后面跟的是分组id）
   * @param {*String} groupId 分组id
   * @param {*Number} sleepState 0-唤醒；1-休眠（非光控唤醒）；2-休眠（光控唤醒）
   */
  async updateSleepStateByGroupId({ commit }, { groupId, sleepState }) {
    try {
      return await http.command.updateSleepStateByGroupId(groupId, sleepState)
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 流量开关（后面跟的是设备id）
   * @param {*Array} deviceId 设备id
   * @param {*String} fourthGenerationFlowSwitchState 4G流量开关；1-开启 0-关闭；
   */
  async updateNetworkBySingle({ commit, dispatch }, { projectId, deviceId, networkType }) {
    try {
      await http.command.updateNetworkBySingle(deviceId, networkType)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
   * 流量开关（后面跟的是设备id）
   * @param {*Array} deviceId 设备id
   * @param {*String} fourthGenerationFlowSwitchState 4G流量开关；1-开启 0-关闭；
   */
  async updateNetworkByGroup({ commit, dispatch }, { projectId, groupId, networkType }) {
    try {
      await http.command.updateNetworkByGroup(groupId, networkType)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },

  /**
     * 切换充放电（单个）
     * @param {*String} projectId 项目id
     * @param {*Array} deviceId 设备id
     * @param {*String} type 操作类型，可选值：1-充电/0-不充电
     */
  async updateRechargeBySingle({ commit, dispatch }, { projectId, deviceId, type }) {
    try {
      await http.command.putPowerSwitch(projectId, { type, deviceId })
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },
   /**
     * 批量充电、断电
     * @param {*String} projectId 项目id
     * @param {*Array} solarMoteIds 设备id
     * @param {*String} type 操作类型，可选值：1-充电/0-不充电
     */
  async chargeBatch({commit, dispatch}, {projectId, solarMoteIds, type}) {
    try {
      await http.command.chargeBatch(projectId, solarMoteIds, type)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  },
  /**
     * 分组下发充电、断电
     * @param {*String} projectId 项目id
     * @param {*Array} solarMoteIds 设备id
     * @param {*String} type 操作类型，可选值：1-充电/0-不充电
     */
  async groupCharging({commit, dispatch}, {projectId, groupId, type}) {
    try {
      await http.command.groupCharging(projectId, groupId, type)
      await new Promise(resolve => setTimeout(() => resolve(), 5000))
      await dispatch('getDeviceList', { projectId })
    } catch (e) {
      throw new Error(e)
    }
  }
}

const mutations = {
  set_strategy_in_group(state, strategy) {
    state.strategyInGroup = state.strategyInGroup.merge(strategy)
  },
  set_strategy_in_device(state, strategy) {
    state.strategyInDevice = state.strategyInDevice.merge(strategy)
  }
}

export default {
  state,
  getters,
  mutations,
  actions
}
